import { ViewerBackend } from './ViewerBackend';
import { jsonRpcCall, rpcOk, rpcPayload } from './jsonRpcCall';
import {
  buildWebSocketUrl,
  buildEviewWebSocketUrl,
  buildEviewJpegUrl,
  buildApiUrl,
} from '../util/getServerInfo';

/**
 * LocalNvrAdapter — ViewerBackend over rda-backend JSON-RPC (the dview/eview
 * local NVR). It centralizes the JSON-RPC envelope (via jsonRpcCall) and the
 * stream-URL builders (via util/getServerInfo) that today are hand-rolled
 * across ~12 action/util files.
 *
 * Migration discipline (Chunk 1, behavior-preserving): each method here is a
 * faithful wrapper of an EXISTING call site — same method name, same params
 * order, same envelope, same global axios (so the noauth interceptor still
 * fires). Methods are filled in as their call sites are read and migrated; the
 * generic `call()` escape hatch lets a call site move behind the adapter in one
 * line before its typed wrapper exists. Unimplemented contract methods inherit
 * ViewerBackend's loud throw.
 */
export class LocalNvrAdapter extends ViewerBackend {
  get kind() {
    return 'local';
  }

  /**
   * Generic JSON-RPC escape hatch. Returns the raw axios response so callers
   * can read `.data.result` exactly as they do today. Prefer a typed method
   * below once one exists; use this to migrate a call site immediately.
   * @param {string} method
   * @param {Array} [params]
   * @param {Object} [opts] - { url, id, timeout }
   * @returns {Promise<import('axios').AxiosResponse>}
   */
  call(method, params, opts) {
    return jsonRpcCall(method, params, opts);
  }

  // ---- auth / session ----------------------------------------------------

  /**
   * info.isAlive — true when the server responds with a truthy payload.
   * Mirrors AuthActions.loginToServer step 1 (id:1, 3s timeout).
   * @param {Object} [opts] - { url, timeout }
   * @returns {Promise<boolean>}
   */
  async isAlive(opts = {}) {
    try {
      const res = await jsonRpcCall('info.isAlive', [], {
        id: 1,
        timeout: opts.timeout ?? 3000,
        url: opts.url,
      });
      return !!rpcPayload(res);
    } catch (_) {
      return false;
    }
  }

  /**
   * info.getSerial — the server's reported serial (used by whichIP to confirm
   * a candidate URL reaches the intended NVR). Returns undefined on failure.
   * @param {Object} [opts] - { url, timeout }
   * @returns {Promise<string|undefined>}
   */
  async getSerial(opts = {}) {
    try {
      const res = await jsonRpcCall('info.getSerial', [], {
        id: 1,
        timeout: opts.timeout ?? 5000,
        url: opts.url,
      });
      return rpcPayload(res);
    } catch (_) {
      return undefined;
    }
  }

  // ---- identity / capability --------------------------------------------

  /**
   * config.meta.getStartup → { hasWebSocket, hasEView, features }.
   * Drop-in for util/checkServerCapabilities (same shape, same failure-safe
   * defaults) so that consumer can later route through the adapter unchanged.
   * @param {string} sSess
   * @param {Object} [opts] - { url, timeout }
   * @returns {Promise<{hasWebSocket:boolean,hasEView:boolean,features:string[]}>}
   */
  async getCapabilities(sSess, opts = {}) {
    const empty = { hasWebSocket: false, hasEView: false, features: [] };
    try {
      const res = await jsonRpcCall('config.meta.getStartup', [sSess], {
        id: 100,
        timeout: opts.timeout ?? 5000,
        url: opts.url,
      });
      if (!rpcOk(res)) return empty;
      const features = rpcPayload(res)?.rgsFeature || [];
      return {
        hasWebSocket: features.includes('websocket'),
        hasEView: features.includes('eview'),
        features,
      };
    } catch (_) {
      return empty;
    }
  }

  /**
   * config.user.getUserByName → the user record (incl. bID), or undefined.
   * Mirrors AuthActions.getPostLoginFromServer (id:15, params [sSess, sName]).
   * @param {string} sSess
   * @param {string} sName
   * @param {Object} [opts] - { url, timeout }
   * @returns {Promise<Object|undefined>}
   */
  async getUserByName(sSess, sName, opts = {}) {
    const res = await jsonRpcCall('config.user.getUserByName', [sSess, sName], {
      id: 15,
      timeout: opts.timeout ?? 6000,
      url: opts.url,
    });
    return rpcPayload(res);
  }

  // ---- stream URL builders (Seam B) -------------------------------------
  // Behavior-preserving delegation to util/getServerInfo. Surfacing them on
  // the adapter is what lets later chunks swap a cloud/EEN URL strategy in
  // without touching call sites.

  /**
   * Live WS URL for a camera. Encapsulates the eview-vs-single-NVR decision that
   * used to live inline in VideoViewWs: an eView member camera (has `bSerial`,
   * eView active) streams from its OWN NVR with per-NVR user:pass fallback auth;
   * otherwise it's this NVR with the session token. The call site passes the
   * camera object + a `ctx` bag and stays mode-agnostic — the branch lives here.
   * @param {Object} camera - camera record ({ bID, bSerial, ... })
   * @param {number} profile - stream profile (1 high / 2 low)
   * @param {Object} [ctx] - { isEview, serverSessions, session, username, password }
   * @returns {string} the WebSocket URL for this camera's transport
   */
  buildLiveUrl(camera, profile, ctx = {}) {
    const { isEview, serverSessions, session, username, password } = ctx;
    if (isEview && camera && camera.bSerial) {
      return this.buildEviewLiveUrl(camera, profile, serverSessions, { session, username, password });
    }
    return buildWebSocketUrl(camera && camera.bID, profile, { session });
  }

  buildEviewLiveUrl(camera, profile, serverSessions, fallbackAuth) {
    return buildEviewWebSocketUrl(camera, profile, serverSessions, fallbackAuth);
  }

  buildEviewJpegUrl(camera, isHighRes, serverSessions, fallbackAuth) {
    return buildEviewJpegUrl(camera, isHighRes, serverSessions, fallbackAuth);
  }

  buildResourceUrl(path) {
    return buildApiUrl(path);
  }

  // ---- not yet migrated (filled per call-site read, Chunk 1 continuation)-
  // login()          → AuthActions.loginToServer / getPostLoginFromServer state machine
  // logout()         → AuthActions logout flow
  // listMemberServers() → config.server.getAllAuthServers  [sSess, sName]
  // listCameras()    → config.camera.getAllCameras / config.user.getCameras
  // listViews/saveView/deleteView → config.user.getSets/addSet/delSet
  // getUserRights()  → config.user.getRights  [sSess, bUserID, bSerial]
  // listEvents()     → PlaybackActions event paging (timestamp-cursor tuple)
  // listPtzPresets() → config.ptz.getAllPresets / getAllConfigs
  // getSystemConfig/getDateTimeConfig → config.system.getAll / config.dtime.getAll
  // buildClipUrl()   → direct pbserver /camstream/?cmd=fetch (PlayerWindow.js)
}
